import React, { useCallback } from 'react'
import { useParams } from 'react-router-dom'
import { useBoQ, useCreateBoQCategory, useImportBoQ, useResetBoQ } from '../hooks/useBoQ'
import { useProject } from '../hooks/useProject'
import { BoQTable } from '../components/table/BoQTable'
import { LoadingSkeleton } from '../components/common/LoadingSkeleton'

interface XLSXImportResponse {
  success: boolean
  projectId: number
  parsed: {
    materials_count: number
    media_tanam_count: number
    trees_count: number
    shrubs_count: number
  }
  imported: {
    categories: number
    items: number
    total: number
  }
  error?: string
}

export const BoQPage: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>()
  const numProjectId = parseInt(projectId || '0')

  const {
    data: boqData,
    isLoading,
    error,
    refetch
  } = useBoQ(numProjectId)

  const { data: projectData } = useProject(numProjectId)

  const createCategoryMutation = useCreateBoQCategory(numProjectId)
  const importMutation = useImportBoQ(numProjectId)
  const resetMutation = useResetBoQ(numProjectId)

  const handleCreateCategory = useCallback(async () => {
    const code = window.prompt('Enter category code (e.g., A, B, C):')
    const name = window.prompt('Enter category name:')

    if (code && name) {
      createCategoryMutation.mutate({
        code,
        name,
        totalBobot: 0,
        sortOrder: (boqData?.categories.length || 0) + 1
      } as any)
    }
  }, [boqData, createCategoryMutation])

  const handleExportCSV = useCallback(() => {
    if (!boqData?.categories) return

    let csvContent = "data:text/csv;charset=utf-8,"
    csvContent += "Category Code,Category Name,Item No,Item Name,Specification,Unit,Qty,Unit Price,Total Price\n"

    boqData.categories.forEach(cat => {
      // Add Category Row
      csvContent += `${cat.code},"${cat.name}",,,,,,\n`
      
      cat.items.forEach((item: any, idx: number) => {
        const itemRow = [
          "", // Cat Code
          "", // Cat Name
          item.itemNumber || idx + 1,
          `"${item.itemName.replace(/"/g, '""')}"`, // Escape quotes
          `"${(item.specification || '').replace(/"/g, '""')}"`,
          item.unit,
          item.quantity,
          item.unitPrice,
          item.totalPrice
        ]
        csvContent += itemRow.join(",") + "\n"
      })
    })

    const encodedUri = encodeURI(csvContent)
    const link = document.createElement("a")
    link.setAttribute("href", encodedUri)
    link.setAttribute("download", `BoQ_Project_${numProjectId}_${new Date().toISOString().split('T')[0]}.csv`)
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }, [boqData, numProjectId])

  const handleImport = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      importMutation.mutate(file)
    }
  }

  const handleImportXLSX = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    
    const formData = new FormData()
    formData.append('file', file)
    
    try {
      const response = await fetch(
        `/api/v1/boq/projects/${numProjectId}/import-xlsx`,
        {
          method: 'POST',
          body: formData,
        }
      )
      
      const result = await response.json() as XLSXImportResponse
      
      if (result.success) {
        console.log(`✅ Success!`)
        console.log(`Parsed: ${result.parsed.materials_count} materials, ${result.parsed.media_tanam_count} media items, ${result.parsed.trees_count} trees, ${result.parsed.shrubs_count} shrubs`)
        console.log(`Imported: ${result.imported.items} items in ${result.imported.categories} categories`)
        refetch()
      } else {
        console.error('Import failed:', result.error)
        alert(`Import failed: ${result.error}`)
      }
    } catch (error) {
      console.error('Upload error:', error)
      alert(`Upload error: ${error}`)
    }
  }

  const handleImportClick = () => {
    // This will be used for the hidden file input
  }

  if (!numProjectId) {
    return (
      <div className="p-8 bg-red-50 border border-red-200 rounded-lg max-w-2xl mx-auto mt-12">
        <p className="text-red-700 font-semibold text-center">Invalid project ID</p>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-slate-50 pb-12">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex justify-between items-start border-b border-slate-200 pb-6">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 tracking-tight">
              {projectData?.name || `Project #${numProjectId}`}
            </h1>
            <p className="text-slate-500 mt-2 text-sm font-medium flex items-center gap-2">
              <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded text-xs uppercase tracking-wide font-bold">
                {projectData?.status || 'Active'}
              </span>
              <span>{projectData?.location || 'Location not set'}</span>
            </p>
          </div>
          <div className="flex gap-3">
            <label className="btn-secondary cursor-pointer">
              <span className="mr-2">📥</span> Import CSV
              <input
                type="file"
                accept=".csv"
                onChange={handleImport}
                className="hidden"
              />
            </label>
            <label className="btn-secondary cursor-pointer">
              <span className="mr-2">📥</span> Import Excel
              <input
                type="file"
                accept=".xlsx"
                onChange={handleImportXLSX}
                className="hidden"
              />
            </label>
            <button
              onClick={handleExportCSV}
              className="btn-secondary"
            >
              <span className="mr-2">📤</span> Export CSV
            </button>
            <button
              onClick={() => {
                if (confirm('Are you sure you want to reset all BoQ data? This will delete all categories and items and cannot be undone.')) {
                  resetMutation.mutate()
                }
              }}
              disabled={resetMutation.isPending}
              className="btn-secondary"
            >
              {resetMutation.isPending ? 'Resetting...' : '🔄 Reset BoQ'}
            </button>
            <button
              onClick={handleCreateCategory}
              disabled={createCategoryMutation.isPending}
              className="btn-primary"
            >
              {createCategoryMutation.isPending ? 'Creating...' : '+ Add Category'}
            </button>
          </div>
        </div>

        {/* Error state */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg shadow-sm">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3 flex-1">
                <p className="text-red-700 font-semibold text-sm">Error loading BoQ</p>
                <p className="text-red-600 text-sm mt-1">{error.message}</p>
                {error.message?.includes('tidak berjalan') && (
                  <div className="mt-3 p-3 bg-white rounded border border-red-200">
                    <p className="text-xs font-medium text-slate-700 mb-2">Cara memperbaiki:</p>
                    <ol className="text-xs text-slate-600 space-y-1 list-decimal list-inside">
                      <li>Buka terminal baru</li>
                      <li>Jalankan: <code className="bg-slate-100 px-1 py-0.5 rounded">npm run dev</code></li>
                      <li>Tunggu sampai backend server berjalan di port 8787</li>
                      <li>Refresh halaman ini</li>
                    </ol>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Table */}
        {boqData && (
          <BoQTable
            projectId={numProjectId}
            categories={boqData.categories}
            isLoading={isLoading}
            onRefresh={() => refetch()}
          />
        )}

        {/* Loading state */}
        {isLoading && !boqData && (
          <div className="card p-6 space-y-4">
            <div className="flex justify-between items-center mb-6">
               <LoadingSkeleton count={1} height="2rem" className="w-1/4" />
               <LoadingSkeleton count={1} height="2rem" className="w-1/6" />
            </div>
            <LoadingSkeleton count={5} height="3rem" />
          </div>
        )}
      </div>
    </div>
  )
}

